import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SeahorseTemplateDirective } from '@seahorse/common';
import { PrimeTemplate } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';
import { buildOptionItems, ShOptionAccessor } from '../select/option-items';

/**
 * Multi select. Same option API as `sh-select`; the value is an array of
 * `optionValue`s (of the options themselves without one). `display` is
 * `'comma'` (default) or `'chip'`. Custom list items:
 * `<ng-template shTemplate="item" let-option>`.

 */
@Component({
  selector: 'sh-multiselect',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MultiSelectModule,
    PrimeTemplate,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShMultiselectComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-multiselect
      [options]="items()"
      optionLabel="label"
      optionValue="value"
      filterBy="label"
      [ngModel]="value()"
      (ngModelChange)="commit($event)"
      [ngModelOptions]="{ standalone: true }"
      [inputId]="controlId()"
      [placeholder]="placeholder() | translate"
      [display]="display()"
      [maxSelectedLabels]="maxLabels()"
      [filter]="filter()"
      [autofocusFilter]="filter()"
      [showClear]="showClear()"
      [size]="pSize()"
      [readonly]="isReadonly()"
      [disabled]="isDisabled()"
      [ariaLabel]="ariaLabel() | translate"
      [fluid]="true"
      [appendTo]="appendTo()"
      (onBlur)="markTouched()"
    >
      <ng-template pTemplate="item" let-option>
        <ng-container
          *ngIf="itemTemplate() as itemTpl; else defaultItem"
          [ngTemplateOutlet]="itemTpl"
          [ngTemplateOutletContext]="{ $implicit: option.raw }"
        ></ng-container>
        <ng-template #defaultItem>{{ option.label }}</ng-template>
      </ng-template>
    </p-multiselect>
  `,
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ShMultiselectComponent<T = any> extends ShFormControl<unknown[]> {
  readonly options = input<readonly T[] | null | undefined>();
  readonly optionLabel = input<ShOptionAccessor<T, string>>();
  readonly optionValue = input<ShOptionAccessor<T, unknown>>();
  readonly display = input<'chip' | 'comma'>('comma');
  /**
   * How many selected labels show before PrimeNG collapses them into
   * "n items selected". Default: all chips in chip mode (PrimeNG's own
   * default of 3 silently hid the rest of the chips), 3 in comma mode.
   */
  readonly maxSelectedLabels = input<number | null>();
  protected readonly maxLabels = computed(
    () => this.maxSelectedLabels() ?? (this.display() === 'chip' ? null : 3)
  );
  readonly filter = input(false, { transform: booleanAttribute });
  readonly showClear = input(false, { transform: booleanAttribute });
  /**
   * Where the overlay is appended: the body by default (no clipping by
   * scrolling containers); `self` keeps it inline, which a control inside a
   * popover or drawer needs, or a click in the overlay counts as an outside
   * click and closes its container.
   */
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');

  private readonly _templates = contentChildren(SeahorseTemplateDirective, {
    descendants: true,
  });

  protected readonly items = computed(() =>
    buildOptionItems(this.options(), this.optionLabel(), this.optionValue())
  );
  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
  protected readonly itemTemplate = computed(
    () => this._templates().find((t) => t.type === 'item')?.templateRef
  );

  // Arrays are equal when they hold the same values in the same order:
  // PrimeNG hands over a new array for every change.
  protected override equals(a: unknown[] | null, b: unknown[] | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
}
